import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  notification,
  Row,
  Col,
  Skeleton,
} from "antd";
import dayjs from "dayjs";
import { toUTCMidnight } from "../utils/dateUtils";
import { DATE_PICKER_FORMAT } from "../constants/dateConstants";
import { useMutation, useQuery } from "@tanstack/react-query";
import { updateTask, fetchTask } from "../services/tasksAPI";
import { useAuth } from "../hooks/useAuth";
import queryClient from "../queryClient";
import { handleIntegerKeyDown, createIntegerChangeHandler } from "../utils/textUtils";
import AttachmentsSection from "./AttachmentsSection";
import SimpleRichTextEditor from "./SimpleRichTextEditor";
import ProjectSettingToggle from "./ProjectSettingToggle";
import ProjectUserSelect from "./ProjectUserSelect";

const PRIORITY_OPTIONS = [
  { value: "High", label: "High" },
  { value: "Medium", label: "Medium" },
  { value: "Low", label: "Low" },
];

const REMINDER_OPTIONS = [
  { value: "None", label: "None" },
  { value: "Daily on weekdays", label: "Daily on weekdays" },
  { value: "Twice a week", label: "Twice a week" },
  { value: "Weekly", label: "Weekly" },
  { value: "Biweekly", label: "Biweekly" },
  { value: "Once a month", label: "Once a month" },
];

const UpdateTaskModal = ({ visible, onClose, task }) => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject.project_id;
  const [form] = Form.useForm();
  const [sendEmail, setSendEmail] = useState(true);
  const statusValue = Form.useWatch("status", form);
  const emailNotificationsDisabled = statusValue ? statusValue !== "Open" : false;

  const { data: fullTask, isLoading: isTaskLoading } = useQuery({
    queryKey: ["task", task?.id],
    queryFn: () => fetchTask(task.id),
    enabled: visible && !!task?.id,
  });

  const taskData = fullTask || task;

  useEffect(() => {
    if (visible && taskData?.id) {
      form.setFieldsValue({
        number: taskData.number,
        title: taskData.title,
        task_detail: taskData.task_detail,
        status: taskData.status,
        priority: taskData.priority || "Medium",
        due_date: taskData.due_date ? dayjs(taskData.due_date) : null,
        task_manager_id: taskData.task_manager_id || taskData.task_manager?.id || taskData.task_manager?._id,
        assignee_ids: taskData.assignee_ids || taskData.assignees?.map((a) => a.id || a._id) || [],
        distribution_list: taskData.distribution_list || [],
        reminder_frequency: taskData.reminder_frequency || "None",
        require_attachment_for_responses: taskData.require_attachment_for_responses || false,
        attachments: taskData.attachments || [],
      });
      const initialSendEmail = taskData.status === "Open";
      setSendEmail(initialSendEmail);
      form.setFieldsValue({ email_notifications: initialSendEmail });
    }
  }, [visible, taskData, form]);

  useEffect(() => {
    if (emailNotificationsDisabled) {
      setSendEmail(false);
      form.setFieldsValue({ email_notifications: false });
    }
  }, [emailNotificationsDisabled, form]);

  const mutation = useMutation({
    mutationFn: ({ taskId, taskData, sendEmail }) => updateTask(taskId, taskData, sendEmail),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks", projectId] });
      queryClient.invalidateQueries({ queryKey: ["task", task?.id] });
      onClose();
      notification.success({ message: "Task updated successfully" });
    },
    onError: (error) => {
      const detail = error.response?.data?.detail;
      notification.error({
        message: "Failed to update task",
        description: detail?.message || detail || error.message,
      });
    },
  });

  const handleUpdate = (values) => {
    const newAssigneeIds = values.assignee_ids || [];
    const newManagerId = String(values.task_manager_id);
    const currentManagerId = String(
      taskData?.task_manager_id || taskData?.task_manager?.id || taskData?.task_manager?._id || "",
    );
    const currentBall = (taskData?.ball_in_court_ids || []).map(String);
    // Match RFI: if ball was exclusively with the previous manager, hand it to the new one.
    const managerCurrentlyHasBall =
      !!currentManagerId &&
      currentBall.length === 1 &&
      currentBall[0] === currentManagerId;

    let reconciledBall;
    if (managerCurrentlyHasBall) {
      reconciledBall = [newManagerId];
    } else {
      const assigneeSet = new Set(newAssigneeIds.map(String));
      reconciledBall = currentBall.filter(
        (uid) => assigneeSet.has(uid) || uid === newManagerId,
      );
      if (reconciledBall.length === 0 && newAssigneeIds.length > 0) {
        reconciledBall = [...newAssigneeIds];
      }
    }

    const payload = {
      ...values,
      attachments: values.attachments?.map((file) => file) || [],
      due_date: values.due_date ? toUTCMidnight(values.due_date) : null,
      ball_in_court_ids: reconciledBall,
    };
    mutation.mutate({ taskId: taskData.id, taskData: payload, sendEmail });
  };

  return (
    <Modal title="Update Task" open={visible} onCancel={onClose} footer={null} className="w-full max-w-4xl">
      {isTaskLoading ? (
        <Skeleton active />
      ) : (
        <Form form={form} onFinish={handleUpdate} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Number"
                name="number"
                rules={[
                  { required: true, message: "Please input the number!" },
                  { pattern: /^\d+$/, message: "Please enter only whole numbers!" },
                ]}
              >
                <Input
                  type="number"
                  step="1"
                  onKeyDown={handleIntegerKeyDown}
                  onChange={createIntegerChangeHandler(form, "number")}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Task Manager" name="task_manager_id" rules={[{ required: true }]}>
                <ProjectUserSelect
                  mode={null}
                  placeholder="Select task manager"
                  className="w-full"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Title" name="title" rules={[{ required: true, message: "Please input the title!" }]}>
            <Input />
          </Form.Item>

          <Form.Item
            label="Task Detail"
            name="task_detail"
            rules={[{ required: true, message: "Please input the task detail!" }]}
          >
            <SimpleRichTextEditor placeholder="Enter task details..." aiEnhanceTarget="task_detail" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Status" name="status">
                <Select
                  options={[
                    { value: "Open", label: "Open" },
                    { value: "Draft", label: "Draft" },
                    { value: "Closed", label: "Closed" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Priority" name="priority">
                <Select options={PRIORITY_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Due Date" name="due_date">
                <DatePicker format={DATE_PICKER_FORMAT} className="w-full" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Reminder Frequency" name="reminder_frequency">
                <Select options={REMINDER_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Assignees"
            name="assignee_ids"
            rules={[
              {
                validator: (_, value) =>
                  value?.length ? Promise.resolve() : Promise.reject(new Error("Please select at least one assignee!")),
              },
            ]}
          >
            <ProjectUserSelect
              placeholder="Select at least one assignee"
              className="w-full"
            />
          </Form.Item>

          <Form.Item label="Distribution List (Cc)" name="distribution_list">
            <ProjectUserSelect
              placeholder="Select Cc"
              className="w-full"
            />
          </Form.Item>

          <Form.Item name="attachments" label="Attachments">
            <AttachmentsSection projectId={projectId} />
          </Form.Item>

          <div className="project-settings-grid">
            <ProjectSettingToggle
              name="require_attachment_for_responses"
              title="Require Attachment for Responses"
            />
          </div>

          <div className="project-settings-grid">
            <ProjectSettingToggle
              name="email_notifications"
              title="Email Notifications"
              description={
                emailNotificationsDisabled
                  ? "Set status to Open to enable emails"
                  : undefined
              }
              initialValue={true}
              checked={sendEmail}
              disabled={emailNotificationsDisabled}
              onChange={(checked) => {
                setSendEmail(checked);
                form.setFieldsValue({ email_notifications: checked });
              }}
            />
          </div>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={mutation.isPending}>
              {sendEmail ? "Update Task and Send Emails" : "Update Task"}
            </Button>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default UpdateTaskModal;
